import { debug } from './debug';

const DEFAULT_CHANNELS = {
  toast: true,
  inApp: false,
  email: false
};

const channelHandlers = {
  toast: null,
  inApp: null,
  email: null
};

const isValidChannel = channel => Object.prototype.hasOwnProperty.call(channelHandlers, channel);

const DEFAULT_USER_ERROR_MESSAGE = 'Something went wrong. Please try again.';

const isLikelyTechnicalMessage = (message = '') => {
  const text = String(message || '').trim();
  if (!text) return false;
  return /(\b(error\s*code|exception|stack\s*trace|rpc|sql|postgres|supabase|failed\s+to\s+fetch|timeout|ECONN|ENOTFOUND|\b\d{3}\b)\b|^\w*Error:)/i.test(text);
};

const sanitizeUserMessage = (message, level = 'info', fallbackMessage = DEFAULT_USER_ERROR_MESSAGE) => {
  const normalized = String(message || '').replace(/\bsupabase\b/ig, 'database').trim();
  if (!normalized) {
    return level === 'error' ? fallbackMessage : '';
  }

  if (level === 'error' && isLikelyTechnicalMessage(normalized)) {
    return fallbackMessage;
  }

  return normalized;
};

const toPayload = (input, options = {}) => {
  const inputPayload = typeof input === 'string' ? { message: input } : (input && typeof input === 'object' ? input : {});
  const channels = {
    ...DEFAULT_CHANNELS,
    ...(inputPayload.channels || {}),
    ...(options.channels || {})
  };

  const merged = {
    ...inputPayload,
    ...options,
    channels,
    level: options.level ?? inputPayload.level ?? 'info',
    message: options.message ?? inputPayload.message ?? inputPayload.userMessage ?? '',
    verboseMessage: options.verboseMessage ?? inputPayload.verboseMessage ?? null,
    blocking: Boolean(options.blocking ?? inputPayload.blocking),
    error: options.error ?? inputPayload.error ?? null,
    fallbackMessage: options.fallbackMessage ?? inputPayload.fallbackMessage ?? DEFAULT_USER_ERROR_MESSAGE
  };

  merged.message = sanitizeUserMessage(merged.message, merged.level, merged.fallbackMessage);

  return merged;
};

const logVerboseDetails = (payload) => {
  const verboseMessage = String(payload?.verboseMessage || '').replace(/\bsupabase\b/ig, 'database').trim();
  const shouldLog = Boolean(payload?.blocking || verboseMessage || payload?.error);
  if (!shouldLog) return;

  const context = payload?.context && typeof payload.context === 'object' ? payload.context : {};
  const errorDetails = payload?.error ? { error: payload.error } : {};

  if (verboseMessage) {
    // eslint-disable-next-line no-console
    console.error('[notify]', payload?.message || 'Notification detail', verboseMessage, { ...context, ...errorDetails });
    return;
  }

  // eslint-disable-next-line no-console
  console.error('[notify]', payload?.message || 'Notification detail', { ...context, ...errorDetails });
};

const tryDispatch = (channel, payload) => {
  if (!payload?.channels?.[channel]) return false;

  const handler = channelHandlers[channel];
  if (typeof handler === 'function') {
    try {
      handler(payload);
      return true;
    } catch (err) {
      return false;
    }
  }

  if (channel === 'toast') {
    const showToast = typeof window !== 'undefined' ? window?.showToast : undefined;
    if (typeof showToast === 'function') {
      try {
        showToast(payload.message);
        return true;
      } catch (err) {
        return false;
      }
    }
  }

  return false;
};

export function notify(input, options = {}) {
  const payload = toPayload(input, options);
  logVerboseDetails(payload);

  if (payload?.channels?.toast) {
    const message = String(payload?.message || '').trim();
    const level = String(payload?.level || '').trim().toLowerCase();
    const isWarningToast = level === 'warn' || level === 'warning';
    const isDismissEvent = Boolean(payload?.dismiss);

    // Only log warning toasts; skip info/loading toasts and dismiss events.
    if (!isDismissEvent && message && isWarningToast) {
      debug('notifyToast', { type: 'warn' }, message);
    }
  }

  return {
    toast: tryDispatch('toast', payload),
    inApp: tryDispatch('inApp', payload),
    email: tryDispatch('email', payload)
  };
}

export function notifySuccess(message, options = {}) {
  return notify({ message, level: 'success' }, options);
}

export function notifyError(message, options = {}) {
  return notify({ message, level: 'error' }, options);
}

export function registerNotificationChannel(channel, handler) {
  if (!isValidChannel(channel) || typeof handler !== 'function') return false;
  channelHandlers[channel] = handler;
  return true;
}

export function unregisterNotificationChannel(channel) {
  if (!isValidChannel(channel)) return false;
  channelHandlers[channel] = null;
  return true;
}

export function notifyWithChannels(input, channels = {}, options = {}) {
  return notify(input, {
    ...options,
    channels: {
      ...(options.channels || {}),
      ...(channels || {})
    }
  });
}

export function notifyToast(input, options = {}) {
  return notifyWithChannels(input, { toast: true, inApp: false, email: false }, options);
}

export function notifyInApp(input, options = {}) {
  return notifyWithChannels(input, { toast: false, inApp: true, email: false }, options);
}

export function notifyEmail(input, options = {}) {
  return notifyWithChannels(input, { toast: false, inApp: false, email: true }, options);
}